import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Mail, Sparkles, ChevronRight, X,
  SlidersHorizontal, BookOpen, RefreshCw,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { usePreferences } from '../hooks/usePreferences'
import { useHouseholdRealtime } from '../hooks/useHouseholdRealtime'
import { getShopUrgency, getEffectivePredictedShop } from '../lib/scheduleAnalyzer'
import type { ScheduleType } from '../lib/scheduleAnalyzer'

// ─── Types ────────────────────────────────────────────────────────────────────

interface HouseholdMember {
  id:                 string
  display_name:       string | null
  email:              string
  is_founding_member: boolean | null
}

interface Household {
  id:                  string
  shopping_day:        number
  auto_list_enabled:   boolean | null
  auto_list_day:       number | null
  auto_list_time:      string | null
  auto_list_email:     string | null
  auto_list_last_sent: string | null
}

const TIME_OPTIONS = [
  { label: 'Morning',   value: '08:00' },
  { label: 'Midday',    value: '12:00' },
  { label: 'Afternoon', value: '15:00' },
  { label: 'Evening',   value: '17:00' },
] as const

// ─── Constants ────────────────────────────────────────────────────────────────

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// Schedule types render as labeled pills with a colored dot — no emoji.
const SCHEDULE_LABELS: Record<ScheduleType, string> = {
  learning:  'Still learning',
  frequent:  'Frequent',
  weekly:    'Weekly',
  biweekly:  'Biweekly',
  irregular: 'Irregular',
  rotating:  'Rotating',
  manual:    'Manual',
}

const SCHEDULE_DOT: Record<ScheduleType, string> = {
  learning:  'bg-ink-muted',
  frequent:  'bg-forest',
  weekly:    'bg-forest',
  biweekly:  'bg-terracotta',
  irregular: 'bg-ink-muted',
  rotating:  'bg-sand',
  manual:    'bg-ink-muted',
}

const AVATAR_TINTS = [
  'bg-forest text-forest-ink',
  'bg-terracotta text-cream',
  'bg-sand text-ink',
  'bg-ok-bg text-forest',
  'bg-cream-warm text-terracotta',
  'bg-paper text-ink border border-border-sand',
]

function avatarTint(name: string): string {
  let hash = 0
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) & 0xffff
  return AVATAR_TINTS[hash % AVATAR_TINTS.length]
}

function avatarLetter(member: HouseholdMember): string {
  const src = member.display_name ?? member.email ?? '?'
  return src.charAt(0).toUpperCase()
}

// ─── Section eyebrow with dotted-line + trailing meta ─────────────────────────

function SectionEyebrow({
  label, meta,
}: { label: React.ReactNode; meta?: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 mt-6 mb-3">
      <h2 className="font-serif text-[14px] italic text-ink">{label}</h2>
      <div className="flex-1 h-px bg-border-sand" />
      {meta && (
        <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-ink-muted">
          {meta}
        </span>
      )}
    </div>
  )
}

// ─── Card wrapper ─────────────────────────────────────────────────────────────

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-paper border border-border-sand rounded-2xl ${className}`}>
      {children}
    </div>
  )
}

// ─── Toggle (matches the rest of the system) ─────────────────────────────────

function Toggle({ on, onChange }: { on: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      aria-pressed={on}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
        on ? 'bg-forest' : 'bg-border-avatar'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-paper shadow-sm transition-transform duration-200 ${
          on ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  )
}

// ─── Household ────────────────────────────────────────────────────────────────

export default function Household() {
  const navigate          = useNavigate()
  const queryClient       = useQueryClient()
  const { user }          = useAuth()
  const { isPremium }     = usePreferences()
  const [searchParams]    = useSearchParams()

  const [editingDay,    setEditingDay]    = useState(false)
  const [editingName,   setEditingName]   = useState(false)
  const [nameInput,     setNameInput]     = useState('')
  const [currentOverride, setCurrentOverride] = useState<number | null>(null)
  const [customDays,      setCustomDays]      = useState('7')
  const [scheduleToast,   setScheduleToast]   = useState('')

  // Auto-list state
  const [autoListEnabled, setAutoListEnabled] = useState(false)
  const [autoListDay,     setAutoListDay]     = useState<number>(5)
  const [autoListTime,    setAutoListTime]    = useState('17:00')
  const [autoListEmail,   setAutoListEmail]   = useState('')
  const [savingAutoList,  setSavingAutoList]  = useState(false)
  const [sendingNow,      setSendingNow]      = useState(false)
  const [autoListToast,   setAutoListToast]   = useState('')

  // Leave household state — controls the button's loading label and the
  // inline error toast below it. Same pattern as scheduleToast above.
  const [isLeavingHousehold, setIsLeavingHousehold] = useState(false)
  const [leaveError,         setLeaveError]         = useState('')

  // ── Fetch user row ─────────────────────────────────────────────────────────

  const { data: userData, refetch: refetchUser } = useQuery({
    queryKey: ['userRow', user?.id],
    enabled:  !!user?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, household_id, display_name, email, restock_notifications_enabled, is_founding_member, founding_member_since')
        .eq('id', user!.id)
        .single()
      if (error) throw error
      return data as {
        id:                            string
        household_id:                  string
        display_name:                  string | null
        email:                         string
        restock_notifications_enabled: boolean | null
        is_founding_member:            boolean | null
        founding_member_since:         string | null
      }
    },
  })

  const householdId = userData?.household_id

  useEffect(() => { document.title = 'Household — Pantry' }, [])

  // ── Fetch household row ────────────────────────────────────────────────────

  const { data: household } = useQuery({
    queryKey: ['household', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('households')
        .select('id, shopping_day, auto_list_enabled, auto_list_day, auto_list_time, auto_list_email, auto_list_last_sent')
        .eq('id', householdId!)
        .single()
      if (error) throw error
      return data as Household
    },
  })

  useEffect(() => {
    if (!household) return
    setAutoListEnabled(household.auto_list_enabled ?? false)
    setAutoListDay(household.auto_list_day ?? household.shopping_day ?? 5)
    setAutoListTime(household.auto_list_time ?? '17:00')
    setAutoListEmail(household.auto_list_email ?? userData?.email ?? '')
  }, [household, userData?.email])

  // ── Fetch members ──────────────────────────────────────────────────────────

  const { data: members = [] } = useQuery({
    queryKey: ['householdMembers', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, display_name, email, is_founding_member')
        .eq('household_id', householdId!)
      if (error) throw error
      return data as HouseholdMember[]
    },
  })

  // ── Fetch shopping schedule ────────────────────────────────────────────────

  const { data: schedule } = useQuery({
    queryKey: ['household-schedule', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('households')
        .select(`
          schedule_type,
          avg_shop_interval_days,
          last_shop_date,
          predicted_next_shop,
          schedule_confidence,
          shop_history,
          schedule_override_days,
          schedule_override_until
        `)
        .eq('id', householdId!)
        .single()
      return data
    },
  })

  const scheduleType = (schedule?.schedule_type ?? 'learning') as ScheduleType
  // For manual schedules with a fixed override interval, the stored
  // predicted_next_shop can be in the past — see getEffectivePredictedShop's
  // docstring. Use the rolled-forward value everywhere this component
  // displays or reasons about the predicted date.
  const effectivePredicted = getEffectivePredictedShop(
    schedule?.predicted_next_shop ?? null,
    scheduleType,
    schedule?.schedule_override_days ?? null,
  )
  const shopUrgency  = getShopUrgency(
    effectivePredicted,
    schedule?.schedule_confidence ?? 0,
  )

  // Realtime — moved to the shared useHouseholdRealtime hook (every
  // user-facing page subscribes through it now). Covers users + invites
  // for member join/leave events and households for shopping_day /
  // auto_list_* changes propagating across members.
  useHouseholdRealtime(householdId, {
    // households uses `id` as its filter column (the primary key IS the
    // household_id; there's no household_id column on this table).
    tables: [
      'users',
      'household_invites',
      { name: 'households', filterColumn: 'id' },
    ],
    invalidateMap: {
      users:             [['householdMembers', householdId], ['userRow']],
      household_invites: [['householdMembers', householdId]],
      households:        [['household', householdId], ['household-schedule', householdId]],
    },
  })

  // ── Leave household ───────────────────────────────────────────────────────
  // Symmetric with the join flow: leaving creates a fresh empty household
  // for the user and moves them into it. All their existing data stays
  // with the household they're leaving (the other members keep using it).
  // Only exposed in the UI when there are 2+ members — leaving a solo
  // household isn't a sensible action.

  async function handleLeaveHousehold() {
    if (!user || !householdId) return

    const otherCount = members.length - 1
    const confirmed = confirm(
      `Leave this household?\n\nYour pantry items, recipes, and lists will stay with ${otherCount} other member${otherCount === 1 ? '' : 's'}. You'll start fresh in a new household.\n\nThis cannot be undone.`
    )
    if (!confirmed) return

    setIsLeavingHousehold(true)
    setLeaveError('')
    try {
      // Generate the UUID client-side and INSERT without .select(). The
      // RETURNING clause from .select() triggers the households SELECT
      // policy, which evaluates against get_user_household_id() — and at
      // the moment of insert that still resolves to the OLD household,
      // so RLS rejects the read with 42501 even though the insert itself
      // is permitted. Skipping .select() avoids the read entirely; we
      // already know the id locally.
      const newHouseholdId = crypto.randomUUID()
      const { error: createError } = await supabase
        .from('households')
        .insert({ id: newHouseholdId })
      if (createError) throw createError

      const { error: updateError } = await supabase
        .from('users')
        .update({ household_id: newHouseholdId })
        .eq('id', user.id)
      if (updateError) throw updateError

      // Reseed the new household_id and refetch the user row so anything
      // depending on those snaps to the new context. For the
      // household-scoped queries we DROP the cache entries instead of
      // invalidating — the cache keys still carry the OLD household_id,
      // and a refetch against that id would hit RLS (we're no longer a
      // member) and bounce back 406s. Dependent components will re-issue
      // queries with the new key on next render.
      queryClient.setQueryData(['householdId', user.id], newHouseholdId)
      await refetchUser()
      queryClient.invalidateQueries({ queryKey: ['userRow'] })
      queryClient.removeQueries({ queryKey: ['household'] })
      queryClient.removeQueries({ queryKey: ['householdMembers'] })
      queryClient.removeQueries({ queryKey: ['household-schedule'] })
      queryClient.removeQueries({ queryKey: ['pantry-items'] })
      queryClient.removeQueries({ queryKey: ['confirmations'] })
      queryClient.removeQueries({ queryKey: ['latestListMeta'] })
      queryClient.removeQueries({ queryKey: ['listItems'] })
      queryClient.removeQueries({ queryKey: ['recipe-suggestions'] })

      navigate('/home')
    } catch (err: any) {
      console.error('[Household] leave error:', err)
      setLeaveError('Could not leave household. Please try again.')
      setIsLeavingHousehold(false)
    }
  }

  // Manual refresh button — escape hatch in case realtime is misconfigured
  // on the project. Forces a refetch of every household-scoped query.
  function handleManualRefresh() {
    refetchUser()
    queryClient.invalidateQueries({ queryKey: ['householdId'] })
    queryClient.invalidateQueries({ queryKey: ['household', householdId] })
    queryClient.invalidateQueries({ queryKey: ['householdMembers', householdId] })
    queryClient.invalidateQueries({ queryKey: ['household-schedule', householdId] })
  }

  // ── Mutations ──────────────────────────────────────────────────────────────

  const updateShoppingDay = useMutation({
    mutationFn: async (day: number) => {
      const { error } = await supabase
        .from('households')
        .update({ shopping_day: day })
        .eq('id', householdId!)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household', householdId] })
      setEditingDay(false)
    },
  })

  const updateDisplayName = useMutation({
    mutationFn: async (name: string) => {
      const { error } = await supabase
        .from('users')
        .update({ display_name: name.trim() })
        .eq('id', user!.id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userRow', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['householdMembers', householdId] })
      setEditingName(false)
    },
  })

  // ── Schedule override ──────────────────────────────────────────────────────

  function showScheduleToast(msg: string) {
    setScheduleToast(msg)
    setTimeout(() => setScheduleToast(''), 3000)
  }

  async function handleOverride(days: number | null) {
    if (!householdId) return
    setCurrentOverride(days)

    if (days === null) {
      await supabase
        .from('households')
        .update({
          schedule_override_days:  null,
          schedule_override_until: null,
          schedule_type:           'learning',
        })
        .eq('id', householdId)
    } else {
      const overrideUntil = new Date()
      overrideUntil.setDate(overrideUntil.getDate() + 90)

      const lastShop = schedule?.last_shop_date
        ? new Date(schedule.last_shop_date)
        : new Date()
      const nextShop = new Date(lastShop)
      nextShop.setDate(nextShop.getDate() + days)

      await supabase
        .from('households')
        .update({
          schedule_override_days:  days,
          schedule_override_until: overrideUntil.toISOString().split('T')[0],
          schedule_type:           'manual',
          avg_shop_interval_days:  days,
          predicted_next_shop:     nextShop.toISOString().split('T')[0],
          schedule_confidence:     1.0,
        })
        .eq('id', householdId)
    }

    queryClient.invalidateQueries({ queryKey: ['household-schedule', householdId] })
    showScheduleToast(days
      ? `Schedule set to every ${days} days`
      : 'Back to automatic schedule learning'
    )
  }

  // ── Auto-list ──────────────────────────────────────────────────────────────

  function showAutoListToast(msg: string) {
    setAutoListToast(msg)
    setTimeout(() => setAutoListToast(''), 3000)
  }

  async function handleSaveAutoList() {
    if (!householdId) return
    setSavingAutoList(true)
    try {
      const { error } = await supabase
        .from('households')
        .update({
          auto_list_enabled: autoListEnabled,
          auto_list_day:     autoListDay,
          auto_list_time:    autoListTime,
          auto_list_email:   autoListEmail.trim(),
        })
        .eq('id', householdId)
      if (error) throw error
      queryClient.invalidateQueries({ queryKey: ['household', householdId] })
      showAutoListToast('Schedule saved')
    } catch (err) {
      console.error('[Household] save auto-list:', err)
      showAutoListToast('Could not save — try again')
    } finally {
      setSavingAutoList(false)
    }
  }

  async function handleSendNow() {
    if (!householdId) return
    setSendingNow(true)
    try {
      const { data: pantryItems } = await supabase
        .from('pantry_items')
        .select('name, display_category')
        .eq('household_id', householdId)
        .eq('is_active', true)
        .limit(20)

      if (!pantryItems || pantryItems.length === 0) {
        showAutoListToast('No items to include in list')
        return
      }

      const itemList = pantryItems
        .slice(0, 15)
        .map(item => `<p style="margin: 6px 0; color: #2B2420; font-family: Georgia, serif;">${item.name}</p>`)
        .join('')

      const html = `
        <div style="font-family: Georgia, serif; max-width: 480px; margin: 0 auto; padding: 24px; background: #F5EFE4;">
          <p style="font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: #9A8B73; font-weight: 500; margin: 0 0 4px 0; font-family: -apple-system, sans-serif;">Your shopping list</p>
          <h2 style="color: #2B2420; margin: 0 0 16px 0; font-weight: normal; font-size: 22px; letter-spacing: -0.015em;">${pantryItems.length} items in your <em>pantry</em></h2>
          <div style="background: #FFFFFF; border-radius: 16px; padding: 20px; border: 1px solid #E8DEC8;">
            ${itemList}
          </div>
          <p style="color: #9A8B73; font-size: 11px; margin-top: 24px; text-align: center; letter-spacing: 0.08em; text-transform: uppercase;">Sent by Pantry</p>
        </div>
      `

      // The Resend API key is held server-side only — by api/send-email.js in
      // prod (process.env.RESEND_API_KEY) and by the Vite dev-proxy in dev
      // (also process.env, never the client). The browser doesn't and
      // shouldn't have access to it; we just hit the relative path.
      // The endpoint requires a valid Supabase JWT — pull it from the
      // current session and forward as Bearer.
      const { data: sessionData } = await supabase.auth.getSession()
      const jwt = sessionData?.session?.access_token
      if (!jwt) {
        showAutoListToast('Please sign in again')
        return
      }
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          from:    'Pantry <lists@pantryioapp.com>',
          to:      [autoListEmail],
          subject: `Your Pantry list · ${pantryItems.length} items`,
          html,
        }),
      })

      const data = await response.json()
      if (response.ok) {
        showAutoListToast('Email sent — check your inbox')
      } else {
        showAutoListToast('Failed: ' + (data.message || 'Unknown error'))
      }
    } catch (err: any) {
      console.error('[Household] send error:', err)
      showAutoListToast('Something went wrong')
    } finally {
      setSendingNow(false)
    }
  }

  // ── Unsubscribe from URL param ─────────────────────────────────────────────

  useEffect(() => {
    if (searchParams.get('unsubscribe') === 'true' && householdId) {
      supabase
        .from('households')
        .update({ auto_list_enabled: false })
        .eq('id', householdId)
        .then(() => {
          setAutoListEnabled(false)
          showAutoListToast('Email list turned off')
          queryClient.invalidateQueries({ queryKey: ['household', householdId] })
        })
    }
  }, [searchParams, householdId, queryClient])

  // ── Sign out ───────────────────────────────────────────────────────────────

  async function handleSignOut() {
    await supabase.auth.signOut()
    navigate('/sign-in')
  }

  // ── Loading state ──────────────────────────────────────────────────────────

  if (!householdId) {
    return (
      <main className="min-h-screen bg-cream pb-24">
        <div className="max-w-[440px] mx-auto px-5 pt-3">
          <div className="h-5 w-28 rounded-lg bg-paper border border-border-sand animate-pulse mb-2" />
          <div className="h-7 w-44 rounded-lg bg-paper border border-border-sand animate-pulse mb-6" />
          <div className="flex flex-col gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-2xl bg-paper border border-border-sand animate-pulse h-24" />
            ))}
          </div>
        </div>
      </main>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <main className="min-h-screen bg-cream pb-24">
      <div className="max-w-[440px] mx-auto px-5">

        {/* Header */}
        <div className="pt-3 pb-2 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium">
              Household
            </p>
            <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mt-0.5">
              Your <em className="italic">household</em>
            </h1>
          </div>
          <button
            type="button"
            onClick={handleManualRefresh}
            aria-label="Refresh household"
            className="mt-3 h-9 w-9 rounded-full border border-border-sand bg-paper text-ink-muted flex items-center justify-center hover:text-ink"
          >
            <RefreshCw className="h-4 w-4" strokeWidth={1.7} />
          </button>
        </div>

        {/* ── Members ───────────────────────────────────────────────────────── */}
        <SectionEyebrow
          label={<>Members</>}
          meta={`${members.length}`}
        />
        <Card>
          <div className="divide-y divide-border-hairline">
            {members.map(member => {
              const label  = member.display_name ?? member.email
              const tint   = avatarTint(label)
              const letter = avatarLetter(member)
              const isMe   = member.id === user?.id
              return (
                <div key={member.id} className="flex items-center gap-3 px-4 py-3">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center font-serif text-[14px] font-medium shrink-0 ${tint}`}>
                    {letter}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-[14px] font-medium text-ink leading-tight truncate">
                      {label}
                      {member.is_founding_member && (
                        <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] font-medium bg-cream-warm text-terracotta align-middle">
                          Founding
                        </span>
                      )}
                      {isMe && (
                        <span className="ml-2 font-serif italic text-[11px] text-ink-muted">
                          (you)
                        </span>
                      )}
                    </p>
                    {member.display_name && (
                      <p className="font-serif italic text-[11px] text-ink-muted truncate mt-0.5">
                        {member.email}
                      </p>
                    )}
                  </div>
                  {!isMe && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!confirm(`Remove ${label} from your household?`)) return
                        // Same RLS workaround as handleLeaveHousehold above
                        // — generate the new id client-side and skip
                        // .select() so RETURNING doesn't trip the
                        // households SELECT policy.
                        const newHouseholdId = crypto.randomUUID()
                        const { error: createError } = await supabase
                          .from('households')
                          .insert({ id: newHouseholdId })
                        if (createError) {
                          console.error('[Household] remove member: create new household failed', createError)
                          return
                        }
                        const { error: updateError } = await supabase
                          .from('users')
                          .update({ household_id: newHouseholdId })
                          .eq('id', member.id)
                        if (updateError) {
                          console.error('[Household] remove member: update users failed', updateError)
                          return
                        }
                        // Drop instead of invalidate — same RLS-on-stale-key
                        // class of bug as handleLeaveHousehold above.
                        queryClient.removeQueries({ queryKey: ['householdMembers'] })
                      }}
                      className="text-[10px] uppercase tracking-[0.1em] font-medium text-terracotta px-2.5 py-1.5 rounded-lg border border-terracotta/30 shrink-0"
                    >
                      Remove
                    </button>
                  )}
                </div>
              )
            })}
          </div>
          {members.length === 1 && (
            <p className="font-serif italic text-[12px] text-ink-muted text-center px-4 py-3 border-t border-border-hairline">
              Just you — invite others from Settings.
            </p>
          )}
        </Card>

        {members.length > 1 && (
          <>
            <button
              type="button"
              onClick={handleLeaveHousehold}
              disabled={isLeavingHousehold}
              className="w-full mt-3 py-2.5 font-serif text-[13px] font-medium text-terracotta border border-terracotta/30 rounded-2xl bg-paper disabled:opacity-50"
            >
              {isLeavingHousehold ? 'Leaving…' : 'Leave household'}
            </button>
            {leaveError && (
              <p className="font-serif italic text-[12px] text-terracotta mt-2 text-center">
                {leaveError}
              </p>
            )}
          </>
        )}

        {/* ── Shopping day ──────────────────────────────────────────────────── */}
        <SectionEyebrow label={<>Shopping <em className="italic">day</em></>} />
        <Card className="px-5 py-4">
          {!editingDay ? (
            <div className="flex items-center justify-between">
              <span className="font-serif text-[15px] text-ink">
                {household ? DAYS[household.shopping_day] : '—'}
              </span>
              <button
                type="button"
                onClick={() => setEditingDay(true)}
                className="text-[10px] uppercase tracking-[0.1em] font-medium text-forest"
              >
                Edit
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-7 gap-1">
                {DAYS.map((day, index) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => updateShoppingDay.mutate(index)}
                    disabled={updateShoppingDay.isPending}
                    className={`py-2 rounded-xl text-[11px] uppercase tracking-[0.06em] font-medium transition-colors ${
                      household?.shopping_day === index
                        ? 'bg-forest text-forest-ink'
                        : 'bg-cream-warm/40 text-ink-muted'
                    }`}
                  >
                    {day.slice(0, 3)}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setEditingDay(false)}
                className="text-[10px] uppercase tracking-[0.1em] font-medium text-ink-muted"
              >
                Cancel
              </button>
            </div>
          )}
        </Card>

        {/* ── Notifications ─────────────────────────────────────────────────── */}
        <SectionEyebrow label={<>Notifications</>} />
        <Card className="px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-serif text-[14px] font-medium text-ink leading-tight">
                Shopping day reminder
              </p>
              <p className="font-serif italic text-[12px] text-ink-muted mt-0.5 leading-[1.4]">
                Get notified the evening before your shop.
              </p>
            </div>
            <Toggle
              on={userData?.restock_notifications_enabled ?? true}
              onChange={async (next) => {
                await supabase
                  .from('users')
                  .update({ restock_notifications_enabled: next })
                  .eq('id', user!.id)
                refetchUser()
              }}
            />
          </div>
        </Card>

        {/* ── Auto Shopping List ────────────────────────────────────────────── */}
        <SectionEyebrow label={<>Auto shopping <em className="italic">list</em></>} />
        {isPremium ? (
          <Card className="px-5 py-4">
            <p className="font-serif italic text-[12px] text-ink-muted mb-3 leading-[1.45]">
              Get your list emailed automatically every week.
            </p>

            <div className="flex items-start justify-between gap-3 py-3 border-b border-border-hairline">
              <div className="flex-1 min-w-0">
                <p className="font-serif text-[14px] font-medium text-ink leading-tight">
                  Auto-generate list
                </p>
                <p className="font-serif italic text-[12px] text-ink-muted mt-0.5 leading-[1.4]">
                  Create and email your list automatically.
                </p>
              </div>
              <Toggle on={autoListEnabled} onChange={setAutoListEnabled} />
            </div>

            {autoListEnabled && (
              <div className="flex flex-col gap-4 pt-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
                    Which day?
                  </p>
                  <div className="grid grid-cols-7 gap-1">
                    {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setAutoListDay(i)}
                        className={`py-2 rounded-xl text-[11px] uppercase tracking-[0.06em] font-medium transition-colors ${
                          autoListDay === i
                            ? 'bg-forest text-forest-ink'
                            : 'bg-cream-warm/40 text-ink-muted'
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
                    What time?
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {TIME_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAutoListTime(opt.value)}
                        className={`rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.08em] font-medium transition-colors ${
                          autoListTime === opt.value
                            ? 'bg-forest text-forest-ink'
                            : 'bg-paper border border-border-sand text-ink-muted'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
                    Send to
                  </p>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" strokeWidth={1.7} />
                    <input
                      type="email"
                      value={autoListEmail}
                      onChange={e => setAutoListEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-border-sand bg-cream font-serif text-[14px] text-ink focus:outline-none focus:border-forest transition-colors"
                    />
                  </div>
                  <p className="font-serif italic text-[11px] text-ink-muted mt-1.5">
                    We'll send your list here.
                  </p>
                </div>

                {household?.auto_list_last_sent && (
                  <p className="font-serif italic text-[11px] text-ink-muted">
                    Last sent: {new Date(household.auto_list_last_sent).toLocaleDateString('en-US', {
                      weekday: 'long', month: 'long', day: 'numeric',
                    })} at {new Date(household.auto_list_last_sent).toLocaleTimeString('en-US', {
                      hour: 'numeric', minute: '2-digit',
                    })}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleSaveAutoList}
                  disabled={savingAutoList || !autoListEmail.trim()}
                  className="w-full rounded-2xl bg-forest text-forest-ink py-3 text-[13px] font-bold tracking-[-0.01em] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingAutoList && (
                    <span className="h-4 w-4 rounded-full border-2 border-forest-ink border-t-transparent animate-spin" />
                  )}
                  {savingAutoList ? 'Saving…' : 'Save schedule'}
                </button>

                <button
                  type="button"
                  onClick={handleSendNow}
                  disabled={sendingNow || !autoListEmail.trim()}
                  className="w-full rounded-2xl border border-border-sand bg-paper py-2.5 font-serif text-[13px] font-medium text-ink-muted disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {sendingNow && (
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-ink-muted border-t-transparent animate-spin" />
                  )}
                  {sendingNow ? 'Sending…' : 'Send test email now'}
                </button>
              </div>
            )}

            {autoListToast && (
              <p className="font-serif italic text-[12px] text-forest mt-3">{autoListToast}</p>
            )}
          </Card>
        ) : (
          <div className="bg-cream-warm border border-sand/40 rounded-2xl px-5 py-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-terracotta" strokeWidth={1.7} />
              <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-terracotta">
                Pantry+
              </p>
            </div>
            <p className="font-serif text-[14px] font-medium text-ink leading-tight">
              Auto shopping <em className="italic">list</em>
            </p>
            <p className="font-serif italic text-[12px] text-ink-muted mt-1.5 leading-[1.5] mb-3">
              Get your list emailed automatically every week.
            </p>
            <button
              type="button"
              onClick={() => navigate('/paywall')}
              className="w-full rounded-2xl bg-forest text-forest-ink py-2.5 text-[13px] font-bold tracking-[-0.01em]"
            >
              Unlock with Pantry+
            </button>
          </div>
        )}

        {/* ── Shopping Schedule ─────────────────────────────────────────────── */}
        <SectionEyebrow label={<>Shopping <em className="italic">schedule</em></>} />
        <Card className="px-5 py-4">
          <div className="mb-4">
            <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
              Detected pattern
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-cream-warm/60 border border-border-sand px-3 py-1.5 font-serif text-[13px] text-ink">
                <span className={`h-1.5 w-1.5 rounded-full ${SCHEDULE_DOT[scheduleType]}`} />
                {(schedule as any)?.description ?? SCHEDULE_LABELS[scheduleType]}
              </span>
              {(schedule?.schedule_confidence ?? 0) > 0 && (
                <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-ink-muted">
                  {Math.round((schedule?.schedule_confidence ?? 0) * 100)}% confident
                </span>
              )}
            </div>
          </div>

          {(schedule?.shop_history ?? []).length > 0 && (
            <div className="mb-4">
              <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
                Recent shops
              </p>
              <div className="flex gap-1.5 flex-wrap">
                {(schedule?.shop_history ?? [])
                  .slice(-10)
                  .reverse()
                  .map((date: string, i: number) => (
                    <div key={date} className="text-center">
                      <div
                        className={`h-8 w-8 rounded-full flex items-center justify-center font-serif text-[12px] font-medium tabular-nums ${
                          i === 0
                            ? 'bg-forest text-forest-ink'
                            : 'bg-cream-warm/40 text-ink-muted border border-border-sand'
                        }`}
                      >
                        {new Date(date).getDate()}
                      </div>
                      <p className="font-serif italic text-[10px] text-ink-muted mt-0.5">
                        {new Date(date).toLocaleDateString('en-US', { month: 'short' })}
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {effectivePredicted && (
            <div className="rounded-xl bg-cream-warm/40 px-3 py-3 mb-4">
              <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-ink-muted">
                Predicted next shop
              </p>
              <p className="font-serif text-[15px] text-ink mt-0.5">
                {new Date(effectivePredicted + 'T00:00:00')
                  .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
              {shopUrgency.daysUntilShop !== null && (
                <p className="font-serif italic text-[12px] text-ink-muted mt-0.5">
                  {shopUrgency.label}
                </p>
              )}
            </div>
          )}

          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
              Override schedule
            </p>
            <p className="font-serif italic text-[11px] text-ink-muted mb-3 leading-[1.4]">
              Set a fixed interval if Pantry hasn't learned your pattern yet.
            </p>
            <div className="flex gap-2 flex-wrap">
              {[
                { days: null, label: 'Auto' },
                { days: 3,    label: 'Every 3 days' },
                { days: 4,    label: 'Every 4 days' },
                { days: 7,    label: 'Weekly' },
                { days: 10,   label: 'Every 10 days' },
                { days: 14,   label: 'Biweekly' },
              ].map(option => {
                const active =
                  currentOverride === option.days ||
                  (option.days === null && schedule?.schedule_override_days == null && scheduleType !== 'manual')
                return (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => handleOverride(option.days)}
                    className={`rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.08em] font-medium transition-colors ${
                      active
                        ? 'bg-forest text-forest-ink'
                        : 'bg-paper border border-border-sand text-ink-muted'
                    }`}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>

            <div className="flex items-center gap-2 mt-3">
              <span className="font-serif italic text-[12px] text-ink-muted">Custom: every</span>
              <input
                type="number"
                min="1"
                max="30"
                value={customDays}
                onChange={e => setCustomDays(e.target.value)}
                className="w-14 px-2 py-1.5 rounded-lg border border-border-sand bg-cream font-serif text-[13px] text-center text-ink outline-none focus:border-forest tabular-nums"
              />
              <span className="font-serif italic text-[12px] text-ink-muted">days</span>
              <button
                type="button"
                onClick={() => {
                  const n = parseInt(customDays)
                  if (!isNaN(n) && n >= 1 && n <= 30) handleOverride(n)
                }}
                className="rounded-lg bg-forest px-3 py-1.5 text-[11px] uppercase tracking-[0.08em] font-bold text-forest-ink"
              >
                Set
              </button>
            </div>

            {scheduleToast && (
              <p className="font-serif italic text-[12px] text-forest mt-3">{scheduleToast}</p>
            )}
          </div>
        </Card>

        {/* ── Account ───────────────────────────────────────────────────────── */}
        <SectionEyebrow label={<>Account</>} />
        <Card>
          <div className="divide-y divide-border-hairline">

            {userData?.is_founding_member && (
              <div className="px-5 py-3.5">
                <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
                  Status
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <Sparkles className="h-4 w-4 text-terracotta" strokeWidth={1.7} />
                  <p className="font-serif text-[14px] text-ink">Founding Member</p>
                </div>
                {userData.founding_member_since && (
                  <p className="font-serif italic text-[12px] text-ink-muted mt-0.5">
                    since {new Date(userData.founding_member_since).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>
            )}

            <div className="px-5 py-3.5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
                Email
              </p>
              <p className="font-serif text-[14px] text-ink mt-0.5">{userData?.email ?? '—'}</p>
            </div>

            <div className="px-5 py-3.5">
              <div className="flex items-baseline justify-between mb-1">
                <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
                  Display name
                </p>
                {!editingName && (
                  <button
                    type="button"
                    onClick={() => { setNameInput(userData?.display_name ?? ''); setEditingName(true) }}
                    className="text-[10px] uppercase tracking-[0.1em] font-medium text-forest"
                  >
                    Edit
                  </button>
                )}
              </div>
              {!editingName ? (
                <p className="font-serif text-[14px] text-ink">
                  {userData?.display_name ?? <span className="font-serif italic text-ink-muted">Not set</span>}
                </p>
              ) : (
                <div className="flex gap-2">
                  <input
                    autoFocus
                    value={nameInput}
                    onChange={e => setNameInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && nameInput.trim()) updateDisplayName.mutate(nameInput)
                      if (e.key === 'Escape') setEditingName(false)
                    }}
                    placeholder="Your name"
                    className="flex-1 px-3 py-1.5 rounded-lg border border-border-sand bg-cream font-serif text-[13px] text-ink focus:outline-none focus:border-forest"
                  />
                  <button
                    type="button"
                    onClick={() => { if (nameInput.trim()) updateDisplayName.mutate(nameInput) }}
                    disabled={!nameInput.trim() || updateDisplayName.isPending}
                    className="rounded-lg bg-forest text-forest-ink px-3 py-1.5 text-[11px] uppercase tracking-[0.08em] font-bold disabled:opacity-40"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingName(false)}
                    className="px-2 py-1.5 rounded-lg text-ink-muted"
                    aria-label="Cancel"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="w-full px-5 py-3.5 flex items-center gap-3 text-left"
            >
              <div className="h-9 w-9 rounded-[10px] bg-cream-warm/60 text-forest flex items-center justify-center flex-shrink-0">
                <SlidersHorizontal className="h-[16px] w-[16px]" strokeWidth={1.7} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-[14px] font-medium text-ink leading-tight">
                  Customize Pantry
                </p>
                <p className="font-serif italic text-[12px] text-ink-muted mt-0.5 leading-[1.4]">
                  Themes and display settings
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-ink-muted flex-shrink-0" strokeWidth={1.5} />
            </button>

            <button
              type="button"
              onClick={() => navigate('/how-it-works')}
              className="w-full px-5 py-3.5 flex items-center gap-3 text-left"
            >
              <div className="h-9 w-9 rounded-[10px] bg-ok-bg text-forest flex items-center justify-center flex-shrink-0">
                <BookOpen className="h-[16px] w-[16px]" strokeWidth={1.7} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-[14px] font-medium text-ink leading-tight">
                  How Pantry works
                </p>
                <p className="font-serif italic text-[12px] text-ink-muted mt-0.5 leading-[1.4]">
                  Tips, colors, and quick actions
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-ink-muted flex-shrink-0" strokeWidth={1.5} />
            </button>

          </div>
        </Card>

        {/* Sign out — terracotta ghost */}
        <button
          type="button"
          onClick={handleSignOut}
          className="w-full mt-4 py-3 font-serif text-[14px] font-medium text-terracotta"
        >
          Sign out
        </button>

      </div>
    </main>
  )
}
